import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {LoadingService} from "../../../services/LoadingService";
import {TOAST_TYPE} from "../../../constants/Tpiq.constants";
import {ToastService} from "../../../services/Toast.service";
import {SwitchBoard} from "../../../services/SwitchBoard";
import {TemplatesService} from "../../../tpiq-services/templates.service";
import {Session} from "../../../services/Session";
import {TpiqSelectDropdownComponent} from "../../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import {FormatAmountPipe} from "../../../pipes/format-amount.pipe";

declare const _: any;
declare const jQuery: any;

@Component({
  selector: 'app-view-template',
  standalone: true,
  imports: [CommonModule, FormsModule, TpiqSelectDropdownComponent, FormatAmountPipe],
  templateUrl: './view-template.component.html',
  styleUrl: './view-template.component.scss'
})
export class ViewTemplateComponent implements OnInit, OnDestroy {
  user: any;
  org: string;
  template: any = {
    id: '',
    rates: [],
    fees: [],
    strategies: []
  };
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  currentView = 'strategies';
  templates: Array<any> = [];
  selectedTemplate: any = '';
  selectedTemplateName: any = '';
  @Input() strategiesList: any;
  @Input() allAssociatedData: any;
  @Output() triggerUseTemplate: EventEmitter<any> = new EventEmitter<any>();
  feesTableColumns: Array<any> = [
    {name: 'description', title: 'Description', type: 'normal', classes: 'pointer zero-overflow'},
    {name: 'amount', title: 'Amount', type: 'normal', classes: 'pointer zero-overflow'},
    {name: 'associatedWithName', title: 'Associated With', type: 'normal', classes: 'pointer zero-overflow'}
  ];
  strategiesTableColumns: Array<any> = [
    {name: 'strategyName', title: 'Strategy', type: 'normal', classes: 'pointer zero-overflow'},
    {name: 'amount', title: 'Amount', type: 'normal', classes: 'pointer zero-overflow'},
    {name: 'associatedWithName', title: 'Associated With', type: 'normal', classes: 'pointer zero-overflow'},
    {name: 'recurring', title: 'Recurring', type: 'normal', classes: 'pointer zero-overflow'},
    {name: 'riskLevelDisplay', title: 'Risk', type: 'normal', classes: 'pointer zero-overflow'}
  ];
  associatedWithData: Array<any> = [
    {id: 'a627ccde-dd57-1090-cefa-fc965526a557', name: 'Effective Rate'},
    {id: 'fba5d8f4-c3f3-46db-aaf4-df85b1945ad3', name: 'State Individual Rate'},
    {id: '82c36191-8d6f-39e7-f711-947591551608', name: 'Marginal Rate'},
    {id: '5a314bc1-4534-0426-1db4-d18403485072', name: 'Cap Gain Rate'},
    {id: '23a5e1b2-03cf-6675-2596-0952387d4b46', name: 'C Corp Flat Rate'},
    {id: 'b870c7b6-802f-11eb-8aa0-1282986503dc', name: 'Non-deductible'},
    {id: '393a6f91-8222-11ef-bd9d-12ff30f54e53', name: 'Full Credit'},
    {id: '454ba189-8222-11ef-bd9d-12ff30f54e53', name: 'N/A'},
    {id: 'f637945b-e78e-11ef-a4a7-0e0adc17208f', name: 'Market Growth Rate'},
    {id: 'ce08f4de-b7f2-449c-9bf8-9674fbf6d0d5', name: 'Qualified Dividends'}];

  constructor(
    private toastService: ToastService,
    private loadingService: LoadingService,
    private switchBoard: SwitchBoard,
    private templatesService: TemplatesService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.switchBoard.onToggleFeedback.next({
      display: false
    });
  }

  ngOnInit(): void {
    this.user = Session.getUser();
    this.org = Session.getOrg();
    this.getTemplatesList();
  }

  getTemplatesList() {
    this.loadingService.triggerLoadingEvent(true);
    this.templatesService.getTemplatesList(this.org, this.user.id)
      .subscribe((response) => {
        this.loadingService.triggerLoadingEvent(false);
        this.templates = response || [];
      }, (error) => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed to get templates');
      });
  }

  getTemplate(){
    this.loadingService.triggerLoadingEvent(true);
    this.templatesService.getTemplate(this.org, this.user.id, this.selectedTemplate).subscribe(response => {
      this.loadingService.triggerLoadingEvent(false);
      this.template = response;
      this.formatFeeLinesData();
      this.formatStrategiesData();
    }, error => {
      this.loadingService.triggerLoadingEvent(false);
      this.toastService.pop(TOAST_TYPE.error, 'Failed to get template info');
    });
  }

  formatFeeLinesData() {
    const base = this;
    this.template.fees = this.template.fees || [];
    _.each(this.template.fees, (fee) => {
      const obj = _.find(base.associatedWithData, {id: fee.associatedWith});
      if (obj) {
        fee.associatedWithName = obj.name;
      }
    });
  }

  formatStrategiesData() {
    const base = this;
    this.template.strategies = this.template.strategies || [];
    _.each(this.template.strategies, (strategy) => {
      const rates = this.allAssociatedData[strategy.strategyId] || [];
      const obj = _.find(rates, {id: strategy.associatedWith});
      if (obj) {
        strategy.associatedWithName = obj.name;
      }
      const strObj = _.find(base.strategiesList, {id: strategy.strategyId});
      if (strObj) {
        strategy.strategyName = strObj.name;
        strategy.strategyType = strObj.type;
        strategy.locked = strObj.locked;
        strategy.riskLevel = strObj.riskLevel;
        strategy.riskLevelDisplay = this.formatRiskLevel(strObj.riskLevel);
      }
    });
  }

  formatRiskLevel(value: any): string {
    const normalized = String(value || '').trim().toLowerCase();
    const labels = {
      'sustained': '1 - Minimal',
      'more likely than not': '2 - Low',
      'substantial authority': '3 - Moderate',
      'reasonable basis': '4 - Heightened',
      'listed/reportable': '5 - Significant'
    };

    return labels[normalized] || String(value || '');
  }

  switchPage(view){
    this.currentView = view;
    jQuery('#a-rates').attr('aria-selected', 'false');
    jQuery('#a-fees').attr('aria-selected', 'false');
    jQuery('#a-strategies').attr('aria-selected', 'false');
    jQuery('#a-' + view).attr('aria-selected', 'true');
  }

  setTemplate(template) {
    if (!_.isEmpty(template)) {
      this.selectedTemplate = template.id;
      this.selectedTemplateName = template.name;
      this.getTemplate();
    }
  }

  useTemplate() {
    this.triggerUseTemplate.emit(this.template);
  }

}

